import { useEffect, useId, useRef, useState } from "react";

import Field from "../../../shared/ui/Field/Field";
import TextInput from "../../../shared/ui/Field/TextInput";
import Select from "../../../shared/ui/Field/Select";
import Textarea from "../../../shared/ui/Field/Textarea";
import InlineError from "../../../shared/ui/Field/InlineError";
import Spinner from "../../../shared/ui/Loading/Spinner";
import Text from "../../../shared/ui/Typography/Text";

import { createClientShare, fetchShareCandidates } from "../api/shares";
import {
  parseDrfFieldErrors,
  messageFromError,
} from "../../../shared/api/drfErrors";
import useDebounce from "../../../hooks/useDebounce";

// ClientShareCreateModal — feature-local T-2 share-create modal.
//
// Issue #181 correction: active≠selected keyboard listbox; Escape once;
// separated initial vs load-more request ownership + retry.

const PERMISSION_OPTIONS = [
  { value: "false", label: "Лише перегляд" },
  { value: "true", label: "Може редагувати" },
];

const EDITABLE_KEYS = ["shared_with", "shared_with_id", "can_edit", "expires_at", "note"];

const INITIAL_DRAFT = {
  shared_with_id: null,
  can_edit: "false",
  expires_at: "",
  note: "",
};

const FALLBACK_AVATAR = "/images/no-profile-picture.png";

const expiresAtForApi = (value) => {
  if (typeof value !== "string" || value.trim().length === 0) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toISOString();
};

const optionDomId = (prefix, candidateId) => `${prefix}-option-${candidateId}`;

const optionClassName = ({ selected, active }) => {
  const classes = ["feature-client-detail__property-option"];
  if (selected) {
    classes.push("feature-client-detail__property-option--selected");
  }
  if (active) {
    classes.push("feature-client-detail__property-option--active");
  }
  return classes.join(" ");
};

function ClientShareCreateModal({ open, onClose, clientId, onSaved }) {
  const reactId = useId();
  const searchInputId = `client-share-recipient-search-${reactId}`;
  const searchHelpId = `client-share-recipient-help-${reactId}`;
  const searchErrorId = `client-share-recipient-error-${reactId}`;
  const listboxId = `client-share-candidate-list-${reactId}`;
  const statusId = `client-share-candidate-status-${reactId}`;
  const loadMoreErrorId = `client-share-load-more-error-${reactId}`;

  const [draft, setDraft] = useState(INITIAL_DRAFT);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [submitState, setSubmitState] = useState("idle");

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [candidates, setCandidates] = useState([]);
  const [candidateState, setCandidateState] = useState("idle");
  const [candidateError, setCandidateError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [listExpanded, setListExpanded] = useState(true);

  // Separated from initial-load ownership (candidateState / retryNonce).
  const [loadMoreState, setLoadMoreState] = useState("idle");
  const [loadMoreError, setLoadMoreError] = useState(null);
  const [failedLoadMorePage, setFailedLoadMorePage] = useState(null);
  const [loadMoreSearchSnapshot, setLoadMoreSearchSnapshot] = useState(null);

  const initialRequestIdRef = useRef(0);
  const initialAbortRef = useRef(null);
  const loadMoreRequestIdRef = useRef(0);
  const loadMoreAbortRef = useRef(null);

  const abortLoadMore = () => {
    if (loadMoreAbortRef.current) {
      loadMoreAbortRef.current.abort();
      loadMoreAbortRef.current = null;
    }
  };

  const resetLoadMoreRecovery = () => {
    abortLoadMore();
    loadMoreRequestIdRef.current += 1;
    setLoadMoreState("idle");
    setLoadMoreError(null);
    setFailedLoadMorePage(null);
    setLoadMoreSearchSnapshot(null);
  };

  useEffect(() => {
    if (open) {
      setDraft(INITIAL_DRAFT);
      setFieldErrors({});
      setFormError(null);
      setSubmitState("idle");
      setSearch("");
      setCandidates([]);
      setCandidateState("idle");
      setCandidateError(null);
      setPage(1);
      setHasMore(false);
      setSelectedCandidate(null);
      setRetryNonce(0);
      setActiveIndex(-1);
      setListExpanded(true);
      resetLoadMoreRecovery();
    } else {
      if (initialAbortRef.current) {
        initialAbortRef.current.abort();
        initialAbortRef.current = null;
      }
      resetLoadMoreRecovery();
    }
    // resetLoadMoreRecovery is stable enough for open transitions; intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const handleDocumentKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (event.defaultPrevented) return;

      // Search-input Escape is handled locally (stopPropagation). This
      // listener covers Escape when focus is outside the combobox.
      event.preventDefault();
      if (activeIndex >= 0) {
        setActiveIndex(-1);
        return;
      }
      onClose();
    };

    document.addEventListener("keydown", handleDocumentKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleDocumentKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose, activeIndex]);

  useEffect(() => {
    if (!open || !clientId) return undefined;

    resetLoadMoreRecovery();

    if (initialAbortRef.current) {
      initialAbortRef.current.abort();
    }
    const controller = new AbortController();
    initialAbortRef.current = controller;
    const requestId = ++initialRequestIdRef.current;
    const searchSnapshot = debouncedSearch;

    setCandidateState("loading");
    setCandidateError(null);
    setPage(1);
    setActiveIndex(-1);

    fetchShareCandidates(clientId, {
      search: searchSnapshot,
      page: 1,
      page_size: 20,
      signal: controller.signal,
    })
      .then((data) => {
        if (requestId !== initialRequestIdRef.current) return;
        if (controller.signal.aborted) return;
        const results = Array.isArray(data?.results) ? data.results : [];
        setCandidates(results);
        setHasMore(Boolean(data?.next));
        setCandidateState("ready");
        setListExpanded(true);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        if (requestId !== initialRequestIdRef.current) return;
        setCandidates([]);
        setHasMore(false);
        setCandidateError(messageFromError(err));
        setCandidateState("error");
      });

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientId, debouncedSearch, retryNonce]);

  useEffect(() => () => {
    if (initialAbortRef.current) {
      initialAbortRef.current.abort();
      initialAbortRef.current = null;
    }
    abortLoadMore();
  }, []);

  // Keep the keyboard-active option in view without scrolling the page.
  useEffect(() => {
    if (activeIndex < 0) return;
    const candidate = candidates[activeIndex];
    if (!candidate) return;
    const el = document.getElementById(optionDomId(listboxId, candidate.id));
    if (!el || typeof el.scrollIntoView !== "function") return;
    el.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [activeIndex, candidates, listboxId]);

  if (!open) return null;

  const submitting = submitState === "submitting";
  const recipientError = fieldErrors.shared_with_id || fieldErrors.shared_with;
  const loadMorePending = loadMoreState === "loading";
  const statusBusy = candidateState === "loading" || loadMorePending || candidateError || loadMoreError;
  const describedBy = [
    searchHelpId,
    recipientError ? searchErrorId : null,
    statusBusy ? statusId : null,
    loadMoreError ? loadMoreErrorId : null,
  ]
    .filter(Boolean)
    .join(" ");

  const activeCandidate = activeIndex >= 0 ? candidates[activeIndex] : null;
  const activeDescendant = activeCandidate
    ? optionDomId(listboxId, activeCandidate.id)
    : undefined;

  const handleFieldChange = (field) => (event) => {
    const next = event.target.value;
    setDraft((prev) => ({ ...prev, [field]: next }));
    setFieldErrors((prev) => {
      if (!prev[field] && !prev._form) return prev;
      const out = { ...prev };
      delete out[field];
      delete out._form;
      return out;
    });
    setFormError(null);
  };

  const handleSelectCandidate = (candidate) => {
    setSelectedCandidate(candidate);
    setDraft((prev) => ({ ...prev, shared_with_id: candidate.id }));
    setActiveIndex(-1);
    setFieldErrors((prev) => {
      if (!prev.shared_with_id && !prev.shared_with && !prev._form) return prev;
      const out = { ...prev };
      delete out.shared_with_id;
      delete out.shared_with;
      delete out._form;
      return out;
    });
    setFormError(null);
  };

  const moveActive = (delta) => {
    if (!candidates.length || candidateState === "loading") return;
    setListExpanded(true);
    setActiveIndex((prev) => {
      if (prev < 0) {
        return delta > 0 ? 0 : candidates.length - 1;
      }
      const next = prev + delta;
      if (next < 0) return candidates.length - 1;
      if (next >= candidates.length) return 0;
      return next;
    });
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveActive(1);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      moveActive(-1);
      return;
    }
    if (event.key === "Enter" && activeIndex >= 0 && candidates[activeIndex]) {
      event.preventDefault();
      handleSelectCandidate(candidates[activeIndex]);
      return;
    }
    if (event.key === "Escape") {
      // Always stop so the document listener cannot double-invoke onClose.
      event.preventDefault();
      event.stopPropagation();
      if (activeIndex >= 0) {
        setActiveIndex(-1);
        return;
      }
      onClose();
    }
  };

  const runLoadMore = async (pageToLoad, searchSnapshot) => {
    if (!clientId || pageToLoad < 2) return;
    if (loadMoreState === "loading" || candidateState === "loading") return;

    abortLoadMore();
    const controller = new AbortController();
    loadMoreAbortRef.current = controller;
    const requestId = ++loadMoreRequestIdRef.current;

    setLoadMoreState("loading");
    setLoadMoreError(null);
    setFailedLoadMorePage(pageToLoad);
    setLoadMoreSearchSnapshot(searchSnapshot);

    try {
      const data = await fetchShareCandidates(clientId, {
        search: searchSnapshot,
        page: pageToLoad,
        page_size: 20,
        signal: controller.signal,
      });
      if (requestId !== loadMoreRequestIdRef.current) return;
      if (controller.signal.aborted) return;

      const results = Array.isArray(data?.results) ? data.results : [];
      setCandidates((prev) => {
        const seen = new Set(prev.map((row) => row.id));
        const appended = results.filter((row) => !seen.has(row.id));
        return [...prev, ...appended];
      });
      setPage(pageToLoad);
      setHasMore(Boolean(data?.next));
      setLoadMoreState("idle");
      setLoadMoreError(null);
      setFailedLoadMorePage(null);
      setLoadMoreSearchSnapshot(null);
    } catch (err) {
      if (controller.signal.aborted) return;
      if (requestId !== loadMoreRequestIdRef.current) return;
      setLoadMoreError(messageFromError(err));
      setLoadMoreState("error");
      setFailedLoadMorePage(pageToLoad);
      setLoadMoreSearchSnapshot(searchSnapshot);
    }
  };

  const handleLoadMore = () => {
    if (!hasMore || loadMorePending || candidateState === "loading") return;
    runLoadMore(page + 1, debouncedSearch);
  };

  const handleRetry = () => {
    if (loadMoreState === "error" && failedLoadMorePage != null) {
      const snapshot = loadMoreSearchSnapshot ?? debouncedSearch;
      setLoadMoreError(null);
      runLoadMore(failedLoadMorePage, snapshot);
      return;
    }
    setCandidateError(null);
    setRetryNonce((n) => n + 1);
  };

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (submitting) return;

    if (draft.shared_with_id == null) {
      setFieldErrors({ shared_with_id: "Оберіть рієлтора зі списку підписок." });
      return;
    }

    setSubmitState("submitting");
    setFieldErrors({});
    setFormError(null);

    try {
      await createClientShare(clientId, {
        shared_with_id: draft.shared_with_id,
        can_edit: draft.can_edit === "true",
        expires_at: expiresAtForApi(draft.expires_at),
        note: draft.note ?? "",
      });
      setSubmitState("saved");
      onSaved();
    } catch (err) {
      const parsed = parseDrfFieldErrors(err, EDITABLE_KEYS);
      if (parsed.shared_with && !parsed.shared_with_id) {
        parsed.shared_with_id = parsed.shared_with;
      }
      setFieldErrors(parsed);
      setFormError(parsed._form || messageFromError(err));
      setSubmitState("error");
    }
  };

  const showInitialEmpty = candidateState !== "loading"
    && !candidateError
    && candidates.length === 0;

  return (
    <div
      className="feature-client-detail__modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="client-share-create-modal-title"
    >
      <div
        className="feature-client-detail__modal-backdrop"
        aria-hidden="true"
        onClick={onClose}
      />
      <div className="feature-client-detail__modal-dialog">
        <header className="feature-client-detail__modal-head">
          <h2
            id="client-share-create-modal-title"
            className="feature-client-detail__modal-title"
          >
            <i className="fa-solid fa-share-nodes" aria-hidden="true" />
            <span>Поділитися клієнтом</span>
          </h2>
          <button
            type="button"
            className="feature-client-detail__modal-close"
            onClick={onClose}
            aria-label="Закрити вікно поділу"
            title="Закрити"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </header>
        <div className="feature-client-detail__modal-body">
          <form className="ui-form" onSubmit={handleSubmit} noValidate>
            <div className="ui-form__rows">
              <div
                className={`ui-field${recipientError ? " ui-field--invalid" : ""}`}
              >
                <label htmlFor={searchInputId} className="ui-field__label">
                  Рієлтор
                  <span className="ui-field__required" aria-hidden="true">*</span>
                </label>
                <span id={searchHelpId} className="ui-field__help">
                  Оберіть користувача, на якого ви зараз підписані.
                </span>
                <div className="ui-field__control">
                  <div className="feature-client-detail__property-picker">
                    <div className="feature-client-detail__property-search">
                      <TextInput
                        id={searchInputId}
                        name="recipient_search"
                        value={search}
                        onChange={(event) => {
                          setSearch(event.target.value);
                          setListExpanded(true);
                          setActiveIndex(-1);
                        }}
                        onKeyDown={handleSearchKeyDown}
                        placeholder="Пошук за ім'ям або @handle…"
                        disabled={submitting}
                        role="combobox"
                        aria-expanded={listExpanded && candidates.length > 0}
                        aria-controls={listboxId}
                        aria-autocomplete="list"
                        aria-activedescendant={activeDescendant}
                        aria-invalid={recipientError ? "true" : undefined}
                        aria-describedby={describedBy || undefined}
                        aria-required="true"
                        autoComplete="off"
                      />
                    </div>

                    <div
                      id={statusId}
                      className="feature-client-detail__property-picker-state"
                      role="status"
                      aria-live="polite"
                    >
                      {candidateError ? (
                        <div className="feature-client-detail__property-picker-error">
                          <InlineError>{candidateError}</InlineError>
                          <button
                            type="button"
                            className="ui-form__button ui-form__button--ghost"
                            onClick={handleRetry}
                          >
                            Повторити
                          </button>
                        </div>
                      ) : null}

                      {loadMoreError ? (
                        <div
                          id={loadMoreErrorId}
                          className="feature-client-detail__property-picker-error"
                        >
                          <InlineError>{loadMoreError}</InlineError>
                          <button
                            type="button"
                            className="ui-form__button ui-form__button--ghost"
                            onClick={handleRetry}
                            disabled={loadMorePending}
                          >
                            Повторити
                          </button>
                        </div>
                      ) : null}

                      {candidateState === "loading" && candidates.length === 0 ? (
                        <Spinner size="sm" label="Завантаження кандидатів" />
                      ) : null}

                      {showInitialEmpty ? (
                        <Text as="p" size="sm" tone="secondary">
                          Немає доступних підписок для спільного доступу.
                        </Text>
                      ) : null}
                    </div>

                    {candidates.length > 0 ? (
                      <ul
                        id={listboxId}
                        className="feature-client-detail__property-options"
                        role="listbox"
                        aria-label="Кандидати для спільного доступу"
                      >
                        {candidates.map((candidate, index) => {
                          const selected =
                            String(draft.shared_with_id) === String(candidate.id);
                          const active = index === activeIndex;
                          const handleLabel = candidate.handle
                            ? `@${candidate.handle}`
                            : "";
                          return (
                            <li key={candidate.id} role="presentation">
                              <button
                                type="button"
                                id={optionDomId(listboxId, candidate.id)}
                                role="option"
                                aria-selected={selected ? "true" : "false"}
                                className={optionClassName({ selected, active })}
                                style={
                                  active && !selected
                                    ? { boxShadow: "inset 0 0 0 2px currentColor" }
                                    : undefined
                                }
                                onClick={() => handleSelectCandidate(candidate)}
                                disabled={submitting || candidateState === "loading"}
                              >
                                <span className="d-flex align-items-center gap-2">
                                  <img
                                    src={candidate.user_image || FALLBACK_AVATAR}
                                    alt=""
                                    width={32}
                                    height={32}
                                    style={{
                                      borderRadius: "50%",
                                      objectFit: "cover",
                                    }}
                                  />
                                  <span className="d-flex flex-column align-items-start">
                                    <span className="feature-client-detail__property-option-title">
                                      {candidate.fullname || "Користувач"}
                                    </span>
                                    {handleLabel ? (
                                      <span className="feature-client-detail__property-option-meta">
                                        {handleLabel}
                                      </span>
                                    ) : null}
                                  </span>
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    ) : null}

                    {hasMore ? (
                      <button
                        type="button"
                        className="ui-form__button ui-form__button--ghost"
                        onClick={handleLoadMore}
                        disabled={
                          loadMorePending
                          || loadMoreState === "error"
                          || candidateState === "loading"
                          || submitting
                        }
                        aria-busy={loadMorePending ? "true" : undefined}
                      >
                        {loadMorePending ? (
                          <Spinner size="sm" label="Завантаження" />
                        ) : (
                          "Показати ще"
                        )}
                      </button>
                    ) : null}

                    {selectedCandidate ? (
                      <Text as="p" size="sm" tone="secondary">
                        Обрано: {selectedCandidate.fullname}
                        {selectedCandidate.handle
                          ? ` (@${selectedCandidate.handle})`
                          : ""}
                      </Text>
                    ) : null}
                  </div>
                </div>
                {recipientError ? (
                  <InlineError id={searchErrorId} className="ui-field__error">
                    {recipientError}
                  </InlineError>
                ) : null}
              </div>

              <Field
                id={`client-share-create-perm-${reactId}`}
                label="Рівень доступу"
                error={fieldErrors.can_edit}
              >
                <Select
                  name="can_edit"
                  value={draft.can_edit}
                  onChange={handleFieldChange("can_edit")}
                  options={PERMISSION_OPTIONS}
                  disabled={submitting}
                />
              </Field>

              <Field
                id={`client-share-create-expires-${reactId}`}
                label="Діє до (опціонально)"
                error={fieldErrors.expires_at}
              >
                <TextInput
                  type="datetime-local"
                  name="expires_at"
                  value={draft.expires_at}
                  onChange={handleFieldChange("expires_at")}
                  disabled={submitting}
                />
              </Field>

              <Field
                id={`client-share-create-note-${reactId}`}
                label="Нотатка"
                error={fieldErrors.note}
              >
                <Textarea
                  name="note"
                  value={draft.note}
                  onChange={handleFieldChange("note")}
                  rows={3}
                  placeholder="Чому ділюсь — контекст для отримувача"
                  disabled={submitting}
                />
              </Field>
            </div>

            <div className="ui-form__messages">
              {formError ? (
                <InlineError className="ui-form__message">
                  {formError}
                </InlineError>
              ) : null}
            </div>

            <div className="ui-form__actions">
              <button
                type="button"
                className="ui-form__button ui-form__button--ghost"
                onClick={onClose}
                disabled={submitting}
              >
                Скасувати
              </button>
              <button
                type="submit"
                className="ui-form__button ui-form__button--primary"
                disabled={submitting}
                data-loading={submitting ? "true" : undefined}
              >
                {submitting ? (
                  <Spinner size="sm" label="Збереження" />
                ) : (
                  "Поділитися"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ClientShareCreateModal;
